import { useCallback, useEffect, useState } from "react";
import FeeDetail from "./FeeDetail";
import { useTranslation } from "react-i18next";
import { SvgIcon } from "@/components/SvgIcon";
import {
  allInPayApp,
  allInPayCard,
  allInPayH5,
  commitOrder,
  wxH5Pay,
} from "@/api/order";
import { message, Form, Button, Modal, Divider, Checkbox } from "antd";
import { getUser } from "@/api/config";
import { allInPayTypeMap, InvoiceTypeMap, PayWay } from "@/utils/contants";
import { mergeOrderListStore, mergeOrderSelectIdsStore } from "@/store/order";
import { useRecoilValue } from "recoil";
import style from "./mobile.module.scss";
import { math } from "@/utils/math";
import { useNavigate } from "react-router";
import { goH5PayHref, goHref, toLogin } from "@/utils/tool";
import * as Sentry from "@sentry/react";
import InvoiceModal from "@/components/Modal/Invoice";
import sentry4Action from "@/utils/sentry";
import OfflineModal from "@/components/Modal/Offline";

type Props = {
  order: Order;
  step: number;
  setStep: (step: number) => void;
};

type TotalInfo = Pick<
  Order,
  | "total_money"
  | "exp_cny_pay_money"
  | "tax_money"
  | "commission_money"
  | "currency"
  | "raw_exp_money"
  | "pay_methods"
  | "pay_status"
  | "order_id"
  | "configuration"
>;

const MobileStep2 = (props: Props) => {
  const { order, setStep, step } = props;
  const { t } = useTranslation();
  const [protocol, setProtocol] = useState(false);
  const [way, setWay] = useState<PayWay>(order.pay_methods[0]);
  const user = getUser();
  const mergeOrderList = useRecoilValue(mergeOrderListStore);
  const mergeOrderSelectIds = useRecoilValue(mergeOrderSelectIdsStore);
  const [moneyList, setMoneyList] = useState<Array<number>>();

  const [payLoading, setPayLoading] = useState(false);
  const [modalInstance, setModalInstance] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [totalInfo, setTotalInfo] = useState<TotalInfo>({
    total_money: order.total_money,
    exp_cny_pay_money: order.exp_cny_pay_money,
    tax_money: order.tax_money,
    commission_money: order.commission_money,
    currency: order.currency,
    raw_exp_money: order.raw_exp_money,
    pay_methods: order.pay_methods,
    pay_status: order.pay_status,
    order_id: order.order_id,
    configuration: order.configuration,
  });

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const [bill, setBill] = useState<(InvoiceInfo & InvoiceAddress) | null>(null);
  /**
   * 确认支付
   */
  const confirmPay = async () => {
    if (!user) {
      window.localStorage.setItem("step", "1");
      toLogin();
      return;
    }
    sentry4Action("确认支付", {
      order_id: order.order_id,
      order_type: order.expense_type,
      publishing_house_name: order.publishing_house_name,
      journal_name: order.journal_name,
      article_title: order.article_title,
      cae: order.contact_author_email.split("@").join(" "),
      meeting_name: order?.meeting_name,
    });
    if (!protocol) {
      messageApi.info(t("请勾选“我已阅读 PubLink 使用协议”"));
      return;
    }

    try {
      if (way === PayWay.Offline) {
        setModalInstance(true);
        return;
      }

      setPayLoading(true);
      const res = await commitOrder<{ transaction_id: string }>({
        bill,
        order_ids: [order.order_id, ...mergeOrderSelectIds],
        pay_money: moneyList,
      });
      setPayLoading(false);
      if (res.code === 0) {
        const transaction_id = res.data.transaction_id;
        if (way === PayWay.Card) {
          cardPayHandle(transaction_id);
        } else if (way === PayWay.Wx) {
          wxPayHandle(transaction_id);
        } else if (way === PayWay.Yinlian) {
          h5PayHandle(transaction_id);
        } else {
          appPayHandle(transaction_id);
        }
      } else if (res.code === 200002) {
        location.reload();
      } else {
        messageApi.error(res.msg);
      }
    } catch (error) {
      Sentry.captureException(new Error("custom error 2"));
    }
  };

  /**
   * 协议选中
   * @param e
   */
  const check = (e) => {
    setProtocol(!protocol);
  };

  /**
   * 微信 H5：打开支付链接，离开页面后进入等待结果页
   */
  const wxPayHandle = async (transaction_id: string) => {
    const wxRes = await wxH5Pay<{ url: string; sandbox: boolean }>({
      transaction_id,
    });
    if (wxRes.code === 0) {
      if (wxRes.data.sandbox) {
        navigate(`/wrap/wait-result/${transaction_id}`);
        return;
      }
      const redirectUrl =
        import.meta.env.VITE_WX_REDIRECT +
        "/wrap/wait-result/" +
        transaction_id;
      const redirectFn = () => {
        if (document.visibilityState === "hidden") {
          document.removeEventListener("visibilitychange", redirectFn);
          navigate(`/wrap/wait-result/${transaction_id}`, {
            replace: true,
          });
        }
      };
      document.addEventListener("visibilitychange", redirectFn);
      goH5PayHref(
        `${wxRes.data.url}&redirect_url=${encodeURIComponent(redirectUrl)}`,
      );
      return;
    }
    messageApi.error(wxRes.msg);
  };

  /**
   * 通联 APP 支付：新标签页打开支付地址，当前页进入等待结果页
   */
  const appPayHandle = async (transaction_id: string) => {
    const payType = allInPayTypeMap[way];
    if (!payType) return;
    const payRes = await allInPayApp<{ url: string; sandbox: boolean }>({
      transaction_id,
      pay_type: payType,
    });
    if (payRes.code === 0) {
      if (payRes.data.sandbox) {
        navigate("/result?transaction_id=" + transaction_id);
        return;
      }
      goHref(payRes.data.url);
      navigate(`/wrap/wait-result/${transaction_id}`, { replace: true });
      return;
    }
    messageApi.error(payRes.msg);
  };

  /**
   * 通联 H5 网关：拿到地址和表单后由浏览器 POST 跳转，拉起云闪付
   */
  const h5PayHandle = async (transaction_id: string) => {
    const payRes = await allInPayH5<{
      url: string;
      sandbox: boolean;
      form: Record<string, string>;
    }>({
      transaction_id,
    });
    if (payRes.code === 0) {
      if (payRes.data.sandbox) {
        navigate("/result?transaction_id=" + transaction_id);
        return;
      }
      submitPayForm(payRes.data.url, payRes.data.form);
      return;
    }
    messageApi.error(payRes.msg);
  };

  /**
   * 通联银行卡支付：拿到地址和表单后由浏览器 POST 跳转
   */
  const cardPayHandle = async (transaction_id: string) => {
    const payRes = await allInPayCard<{
      url: string;
      sandbox: boolean;
      form: Record<string, string>;
    }>({
      transaction_id,
    });
    if (payRes.code === 0) {
      if (payRes.data.sandbox) {
        navigate("/result?transaction_id=" + transaction_id);
        return;
      }
      submitPayForm(payRes.data.url, payRes.data.form);
      return;
    }
    messageApi.error(payRes.msg);
  };

  const submitPayForm = (url: string, form: Record<string, string>) => {
    const formEl = document.createElement("form");
    formEl.method = "POST";
    formEl.action = url;
    formEl.acceptCharset = "UTF-8";
    Object.entries(form || {}).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      formEl.appendChild(input);
    });
    document.body.appendChild(formEl);
    formEl.submit();
  };

  /**
   * 计算总金额
   */
  useEffect(() => {
    const ids = mergeOrderSelectIds.join(",");
    const selectOrders = ids
      ? mergeOrderList.filter((item) => ids.includes(item.order_id))
      : [];
    const moneyList = [order.total_money];
    const total = {
      total_money: order.total_money,
      exp_cny_pay_money: order.exp_cny_pay_money,
      tax_money: order.tax_money,
      commission_money: order.commission_money,
      currency: order.currency,
      raw_exp_money: order.raw_exp_money,
      pay_methods: order.pay_methods,
      pay_status: order.pay_status,
      order_id: order.order_id,
      configuration: order.configuration,
    };
    for (let i = 0; i < selectOrders.length; i++) {
      const element = selectOrders[i];
      const { total_money, exp_cny_pay_money, tax_money, commission_money } =
        element;
      total.total_money = math
        .add(math.bignumber(total.total_money), math.bignumber(total_money))
        .toNumber();
      total.exp_cny_pay_money = math
        .add(
          math.bignumber(total.exp_cny_pay_money),
          math.bignumber(exp_cny_pay_money),
        )
        .toNumber();
      total.tax_money = math
        .add(math.bignumber(total.tax_money), math.bignumber(tax_money))
        .toNumber();
      total.commission_money = math
        .add(
          math.bignumber(total.commission_money),
          math.bignumber(commission_money),
        )
        .toNumber();

      moneyList.push(element.total_money);
    }

    setTotalInfo({ ...total });
    setMoneyList(moneyList);
  }, [order, mergeOrderList, mergeOrderSelectIds]);

  const getModalInstance = useCallback(
    async (order: Order, mergeOrderSelectIds: string[]) => {
      if (!user) {
        window.localStorage.setItem("step", "1");
        toLogin();
        return;
      }
      sentry4Action("填写发票", {
        order_id: order.order_id,
        order_type: order.order_type,
        publishing_house_name: order.publishing_house_name,
        journal_name: order.journal_name,
        article_title: order.article_title,
        cae: order.contact_author_email.split("@").join(" "),
        meeting_name: order?.meeting_name,
      });
      if (order.pay_status == 1) {
        setShowInvoiceModal(true);
      }
    },
    [bill],
  );

  return (
    <>
      {contextHolder}
      <div
        className={style["mobile-content-step2"]}
        style={{ display: `${step === 1 ? "flex" : "none"}` }}
      >
        <div className={style["fee-box"]}>
          <FeeDetail
            order={totalInfo}
            way={way}
            setWay={setWay}
            bill={bill}
            mergeOrderSelectIds={mergeOrderSelectIds}
            moneyList={moneyList}
          />
          {/* {!user && order.pay_status !== 2 && <UserInput form={form} />} */}
          <div>
            {order.configuration.enable_request_bill &&
              order.pay_status !== 2 &&
              (!bill ? (
                <div
                  className={style.invoice}
                  onClick={() => getModalInstance(order, mergeOrderSelectIds)}
                >
                  <span>{t("发票服务")}</span>
                  <span>
                    <SvgIcon icon="icon-tianxie" width={14} height={14} />
                    <span>{t("申请发票")}</span>
                  </span>
                </div>
              ) : (
                <div className={style["invoice-edit"]}>
                  <div className={style["btn-line"]}>
                    <div>{t("发票服务")}</div>
                    <div className={style.edit}>
                      <span
                        className={style.btn}
                        onClick={() =>
                          getModalInstance(order, mergeOrderSelectIds)
                        }
                      >
                        <SvgIcon icon="icon-tianxie" width={14} height={14} />
                        {t("修改")}
                      </span>
                      <Divider type="vertical" />
                      <span className={style.btn} onClick={() => setBill(null)}>
                        <SvgIcon icon="icon-shanchu" width={14} height={14} />
                        {t("清除")}
                      </span>
                    </div>
                  </div>
                  <Divider className={style["h-divider"]} />
                  <div className={style["text-line"]}>
                    <div>
                      <span className={style.title}>{t("发票类型")}</span>
                      <span className={style.value}>
                        {t(InvoiceTypeMap[bill.bill_type])}
                        {/* {bill.bill_type === 1
                          ? t("电子增值税发票")
                          : t("增值税专用发票")} */}
                      </span>
                    </div>
                    <div>
                      <span className={style.title}>{t("抬头名称")}</span>
                      <span className={style.value}>{bill.title_title}</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
        <div className={style.btn_area}>
          <Button
            type="primary"
            className={`confirm-pay ${
              order.pay_status == 2 ? style["element-disable"] : ""
            }`}
            onClick={confirmPay}
            // loading={payLoading}
            disabled={order.pay_status !== 1}
          >
            {order.pay_status == 1
              ? t("确认支付")
              : order.pay_status == 2
                ? t("订单已支付")
                : t("订单已关闭或已取消")}
          </Button>
          <Checkbox
            className="protocol"
            checked={protocol}
            onChange={check}
            disabled={order.pay_status === 2}
          >
            <span> {t("我已阅读并同意 PubLink")} </span>
            <a className="light" href="agreement/use.html" target="_blank">
              {t("使用协议")}
            </a>
            <span> {t("和")} </span>
            <a className="light" href="agreement/privacy.html" target="_blank">
              {t("隐私协议")}
            </a>
          </Checkbox>
        </div>
      </div>
      {modalInstance}

      {showInvoiceModal && (
        <InvoiceModal
          hiddenModal={() => setShowInvoiceModal(false)}
          invoiceInfo={bill}
          getInvoiceInfo={(info) => {
            let filteredObj = { ...bill, ...info };
            if (filteredObj.bill_type === 1) {
              filteredObj = Object.keys(filteredObj).reduce(
                (acc, key) => {
                  if (!key.includes("address") || key === "title_address") {
                    acc[key] = filteredObj[key];
                  }
                  return acc;
                },
                {} as InvoiceInfo & InvoiceAddress,
              );
            }
            setBill({ ...filteredObj });
          }}
          orderIds={[order.order_id, ...mergeOrderSelectIds]}
          additionalParams={{ orderType: order.order_type }}
        />
      )}
      {modalInstance && (
        <OfflineModal
          hiddenModal={() => setModalInstance(false)}
          actionParams={{
            bill,
            order_ids: [order.order_id, ...mergeOrderSelectIds],
            pay_money: moneyList,
          }}
        />
      )}
    </>
  );
};

export default MobileStep2;
